import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { waLink, telLink, mapsLink } from "@/lib/business";
import { trackBusinessEvent } from "@/lib/tracking";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import {
    MessageCircle, Phone, MapPin, Clock, Instagram, Facebook, Globe,
    Star, Loader2, Store, ExternalLink, Pencil, X, Send,
} from "lucide-react";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&q=80";

export default function PublicBusinessPage() {
    const { slug } = useParams();
    const { t } = useI18n();
    const [biz, setBiz] = useState(undefined);
    const [reviewOpen, setReviewOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try { setBiz((await api.get(`/public/business/${slug}`)).data); }
            catch { setBiz(null); }
        })();
    }, [slug]);

    if (biz === undefined) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>;
    }
    if (biz === null) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
                <Store className="w-10 h-10 text-muted-foreground" />
                <h1 className="font-heading text-2xl font-semibold mt-4">{t("pb_not_found_title")}</h1>
                <p className="text-muted-foreground mt-2">{t("pb_not_found_sub")}</p>
                <Link to="/" className="mt-6 text-accent font-medium">{t("pb_go_home")}</Link>
            </div>
        );
    }

    const accent = biz.accent_color || "#4f6cd6";
    const track = (type) => trackBusinessEvent(slug, type);
    const products = (biz.products || []).filter((p) => p.name);
    const services = (biz.services || []).filter((s) => s.name);
    const why = (biz.why_choose_us || []).filter(Boolean);
    const gallery = (biz.gallery_images || []).filter(Boolean);
    const testimonials = (biz.testimonials || []).filter((rev) => rev.name);
    const openHours = (biz.hours || []).filter((h) => h.day);
    const hasWa = !!biz.whatsapp, hasTel = !!biz.phone;
    const waMsg = biz.whatsapp_message || `Hi, I visited your website (${biz.name}) and I'm interested.`;

    const style = { "--biz-accent": accent };

    const onReviewPosted = (review) => {
        setBiz((b) => ({ ...b, testimonials: [...(b.testimonials || []), review] }));
    };

    return (
        <div className="relative min-h-screen bg-white text-slate-900" style={style} data-testid="public-business-page">
            {/* Navbar */}
            <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-100">
                <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                        {biz.logo_url ? (
                            <img src={biz.logo_url} alt={biz.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: accent }}>
                                {biz.name?.[0] || "B"}
                            </div>
                        )}
                        <span className="font-semibold truncate">{biz.name}</span>
                    </div>
                    <nav className="hidden sm:flex items-center gap-6 text-sm text-slate-600">
                        {biz.about && <a href="#about" className="hover:text-slate-900">{t("pb_about")}</a>}
                        {products.length > 0 && <a href="#products" className="hover:text-slate-900">{t("pb_products")}</a>}
                        {services.length > 0 && <a href="#services" className="hover:text-slate-900">{t("pb_services")}</a>}
                        {gallery.length > 0 && <a href="#gallery" className="hover:text-slate-900">{t("pb_gallery")}</a>}
                        <a href="#contact" className="hover:text-slate-900">{t("pb_contact")}</a>
                    </nav>
                    <div className="flex items-center gap-2">
                        <LanguageSwitcher variant="ghost" testid="pb-lang" />
                        {hasWa && (
                            <a href={waLink(biz.whatsapp, waMsg)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp")}
                                className="hidden sm:inline-flex items-center gap-1.5 text-white text-sm font-medium rounded-full px-4 py-2" style={{ background: accent }} data-testid="nav-whatsapp-btn">
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                        )}
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative">
                <div className="h-[280px] sm:h-[420px] w-full overflow-hidden bg-slate-100">
                    <img src={biz.hero_image_url || FALLBACK_HERO} alt={biz.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-10">
                    <div className="max-w-5xl mx-auto text-white">
                        <span className="inline-block text-[11px] font-medium uppercase tracking-wide rounded-full px-3 py-1" style={{ background: accent }}>{biz.category}</span>
                        <h1 className="font-heading text-3xl sm:text-5xl font-semibold mt-3 leading-tight">{biz.name}</h1>
                        {biz.tagline && <p className="mt-2 text-base sm:text-lg text-white/90 max-w-2xl">{biz.tagline}</p>}
                        {(biz.city || biz.area) && (
                            <p className="mt-2 text-sm text-white/80 inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {[biz.area, biz.city].filter(Boolean).join(", ")}</p>
                        )}
                        <div className="mt-5 flex flex-wrap gap-2.5">
                            {hasWa && <a href={waLink(biz.whatsapp, waMsg)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp")} className="inline-flex items-center gap-2 text-white font-medium rounded-full px-5 py-2.5 text-sm" style={{ background: accent }} data-testid="hero-whatsapp-btn"><MessageCircle className="w-4 h-4" /> {t("pb_whatsapp_us")}</a>}
                            {hasTel && <a href={telLink(biz.phone)} onClick={() => track("call")} className="inline-flex items-center gap-2 bg-white text-slate-900 font-medium rounded-full px-5 py-2.5 text-sm" data-testid="hero-call-btn"><Phone className="w-4 h-4" /> {t("pb_call_now")}</a>}
                            <a href={mapsLink(biz)} target="_blank" rel="noreferrer" onClick={() => track("map")} className="inline-flex items-center gap-2 bg-white/15 backdrop-blur text-white border border-white/40 font-medium rounded-full px-5 py-2.5 text-sm" data-testid="hero-directions-btn"><MapPin className="w-4 h-4" /> {t("pb_directions")}</a>
                        </div>
                    </div>
                </div>
            </section>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-28 sm:pb-16">
                {/* About */}
                {biz.about && (
                    <section id="about" className="py-12 scroll-mt-16">
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold">{t("pb_about")} {biz.name}</h2>
                        <p className="mt-4 text-slate-600 leading-relaxed whitespace-pre-line max-w-3xl">{biz.about}</p>
                    </section>
                )}

                {/* Products */}
                {products.length > 0 && (
                    <section id="products" className="py-10 scroll-mt-16">
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold">{t("pb_our_products")}</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                            {products.map((p) => (
                                <ItemCard key={p.id} item={p} accent={accent} biz={biz} track={track} kind="product" t={t} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Services */}
                {services.length > 0 && (
                    <section id="services" className="py-10 scroll-mt-16">
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold">{t("pb_our_services")}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                            {services.map((s) => (
                                <ItemCard key={s.id} item={s} accent={accent} biz={biz} track={track} kind="service" t={t} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Why choose us */}
                {why.length > 0 && (
                    <section className="py-10">
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold">{t("pb_why")}</h2>
                        <div className="grid sm:grid-cols-3 gap-4 mt-6">
                            {why.map((w, i) => (
                                <div key={i} className="rounded-2xl border border-slate-100 p-5 bg-slate-50/60">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ background: accent }}>{i + 1}</div>
                                    <p className="mt-3 text-slate-700 font-medium">{w}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Gallery */}
                {gallery.length > 0 && (
                    <section id="gallery" className="py-10 scroll-mt-16">
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold">{t("pb_gallery")}</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
                            {gallery.map((g, i) => (
                                <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-slate-100">
                                    <img src={g} alt={`${biz.name} ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Testimonials + write review */}
                <section id="reviews" className="py-10 scroll-mt-16">
                    <div className="flex items-end justify-between gap-3 flex-wrap">
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold">{t("pb_reviews")}</h2>
                        <button
                            onClick={() => setReviewOpen(true)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-4 py-2 text-white"
                            style={{ background: accent }}
                            data-testid="write-review-btn"
                        >
                            <Pencil className="w-4 h-4" /> {t("pb_write_review")}
                        </button>
                    </div>
                    {testimonials.length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                            {testimonials.map((rev) => (
                                <div key={rev.id} className="rounded-2xl border border-slate-100 p-5" data-testid="public-review-card">
                                    <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < (rev.rating || 5) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div>
                                    <p className="mt-3 text-slate-600 text-sm leading-relaxed">“{rev.comment}”</p>
                                    <div className="mt-3 text-sm font-medium">{rev.name}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 mt-4">{t("rv_sub")}</p>
                    )}
                </section>

                {/* Location + Hours */}
                <section id="contact" className="py-10 grid md:grid-cols-2 gap-6 scroll-mt-16">
                    <div className="rounded-2xl border border-slate-100 p-6">
                        <h3 className="font-heading text-xl font-semibold flex items-center gap-2"><MapPin className="w-5 h-5" style={{ color: accent }} /> {t("pb_visit_us")}</h3>
                        <p className="mt-3 text-slate-600">{[biz.address, biz.area, biz.city].filter(Boolean).join(", ") || t("pb_address_available")}</p>
                        <a href={mapsLink(biz)} target="_blank" rel="noreferrer" onClick={() => track("map")} className="mt-4 inline-flex items-center gap-2 text-sm font-medium rounded-full px-4 py-2 text-white" style={{ background: accent }} data-testid="contact-directions-btn">
                            {t("pb_get_directions")} <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    </div>
                    <div className="rounded-2xl border border-slate-100 p-6">
                        <h3 className="font-heading text-xl font-semibold flex items-center gap-2"><Clock className="w-5 h-5" style={{ color: accent }} /> {t("pb_opening_hours")}</h3>
                        <ul className="mt-3 space-y-1.5 text-sm">
                            {openHours.map((h) => (
                                <li key={h.day} className="flex justify-between">
                                    <span className="text-slate-600">{h.day}</span>
                                    <span className="font-medium">{h.closed ? t("closed") : `${h.open || "—"} – ${h.close || "—"}`}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* Social + contact row */}
                <section className="py-6 flex flex-wrap items-center gap-3">
                    {biz.social_instagram && <a href={biz.social_instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm"><Instagram className="w-4 h-4" /> Instagram</a>}
                    {biz.social_facebook && <a href={biz.social_facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm"><Facebook className="w-4 h-4" /> Facebook</a>}
                    {biz.social_website && <a href={biz.social_website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm"><Globe className="w-4 h-4" /> Website</a>}
                    {biz.email && <a href={`mailto:${biz.email}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm">{biz.email}</a>}
                </section>
            </main>

            {/* Footer */}
            <footer className="border-t border-slate-100 py-8 text-center text-sm text-slate-500">
                <div className="font-semibold text-slate-700">{biz.name}</div>
                <div className="mt-1">{[biz.area, biz.city].filter(Boolean).join(", ")}</div>
                {biz.plan !== "business" && (
                    <div className="mt-4 text-xs">
                        <Link to="/" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-600">
                            <div className="w-4 h-4 rounded-full sphere" /> {t("pb_powered_by")}
                        </Link>
                    </div>
                )}
            </footer>

            {/* Mobile sticky CTA bar */}
            <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 p-2.5 flex gap-2">
                {hasWa && <a href={waLink(biz.whatsapp, waMsg)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp")} className="flex-1 inline-flex items-center justify-center gap-1.5 text-white font-medium rounded-xl py-3 text-sm" style={{ background: accent }} data-testid="sticky-whatsapp-btn"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
                {hasTel && <a href={telLink(biz.phone)} onClick={() => track("call")} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white font-medium rounded-xl py-3 text-sm" data-testid="sticky-call-btn"><Phone className="w-4 h-4" /> {t("pb_call")}</a>}
                <a href={mapsLink(biz)} target="_blank" rel="noreferrer" onClick={() => track("map")} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-slate-200 font-medium rounded-xl py-3 text-sm" data-testid="sticky-map-btn"><MapPin className="w-4 h-4" /> {t("pb_map")}</a>
            </div>

            {reviewOpen && (
                <ReviewModal
                    slug={slug}
                    accent={accent}
                    t={t}
                    onClose={() => setReviewOpen(false)}
                    onPosted={onReviewPosted}
                />
            )}
        </div>
    );
}

function ItemCard({ item, accent, biz, track, kind, t }) {
    const msg = `Hi, I'm interested in "${item.name}" listed on your website (${biz.name}).`;
    return (
        <div className="rounded-2xl border border-slate-100 overflow-hidden flex flex-col">
            {item.image_url ? (
                <div className="aspect-square bg-slate-100 overflow-hidden">
                    <img src={item.image_url} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                </div>
            ) : (
                <div className="aspect-square flex items-center justify-center text-white text-3xl font-semibold" style={{ background: accent }}>{item.name?.[0]}</div>
            )}
            <div className="p-3.5 flex flex-col flex-1">
                <div className="font-semibold text-sm leading-tight">{item.name}</div>
                {item.price != null && item.price !== "" && <div className="mt-1 font-semibold" style={{ color: accent }}>₹{Number(item.price).toLocaleString("en-IN")}</div>}
                {item.description && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{item.description}</p>}
                {biz.whatsapp && (
                    <a href={waLink(biz.whatsapp, msg)} target="_blank" rel="noreferrer" onClick={() => track("product")}
                        className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg py-2 border" style={{ borderColor: accent, color: accent }} data-testid={`${kind}-enquire-btn`}>
                        <MessageCircle className="w-3.5 h-3.5" /> {t("pb_enquire")}
                    </a>
                )}
            </div>
        </div>
    );
}

function ReviewModal({ slug, accent, t, onClose, onPosted }) {
    const [name, setName] = useState("");
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const nm = name.trim();
        const cm = comment.trim();
        if (nm.length < 1) { toast.error(t("rv_min_name")); return; }
        if (cm.length < 3) { toast.error(t("rv_min_comment")); return; }
        setLoading(true);
        try {
            const { data } = await api.post(`/public/business/${slug}/review`, { name: nm, rating, comment: cm });
            toast.success(t("rv_posted"));
            onPosted?.(data.review);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="review-modal">
            <div
                className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 fade-up max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="font-heading text-xl font-semibold">{t("rv_title")}</h3>
                        <p className="text-sm text-slate-500 mt-1">{t("rv_sub")}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-900 shrink-0" data-testid="review-close-btn">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={submit} className="mt-5 space-y-4">
                    <div>
                        <Label>{t("rv_your_name")}</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-xl" placeholder="Anita" data-testid="rv-name-input" />
                    </div>
                    <div>
                        <Label>{t("rv_your_rating")}</Label>
                        <div className="mt-1.5 flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`rv-star-${n}`}>
                                    <Star className={`w-7 h-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label>{t("rv_your_review")}</Label>
                        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} className="mt-1.5 rounded-xl" placeholder="…" data-testid="rv-comment-input" />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full rounded-full h-11 text-white" style={{ background: accent }} data-testid="rv-submit-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> {t("rv_submit")}</>}
                    </Button>
                </form>
            </div>
        </div>
    );
}
